/**
 * faqs.js
 *
 * @description The FAQs page.
 * @namespace faqs
 * @since 3.0.0
 * @author jsaldivar
 */

import { i18n, loading, outputDiv } from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";

/**
 * Renders the FAQs page based on the provided parameters.
 *
 * @param {Object} params - The parameters for rendering the FAQs page.
 * @param {string} params.slug - The slug of the page.
 * @param {Array} params.pages - The array of pages.
 */
export const faqsPage = (params) => {
    console.log("[FAQs] ...");

    const slug = params.slug;
    const page = params.pages.find((page) => page.PageName === slug);

    const pageTitle = createElement(
        "h2",
        {
            class: "page-title mt-2 mb-4",
            "aria-label": i18n.getTranslation("faq_page_aria_title"),
        },
        [],
        page.PageTitle
    );
    const pageContent = createElement("div", { class: "page-content" });

    if (page.PageContent.length > 0) {
        renderFaqs(page, pageContent);
    }

    const pageDiv = createElement(
        "div",
        {
            class: "faq-container",
        },
        [pageTitle, pageContent]
    );

    outputDiv.appendChild(pageDiv);
    loadingGif(loading, "hide", 100);

    // FAQ Linking
    faqLinking();
};

/**
 * Loops through the faqs sections of a page and appends them to the page content.
 *
 * @param {Object} page - The page object.
 * @param {HTMLElement} pageContent - The container element for the page content.
 */
export function renderFaqs(page, pageContent) {
    let listCount = 0;
    let sectionCount = 0;
    page.PageContent.forEach((section) => {
        sectionCount++;

        const sectionTitle = createElement(
            "div",
            {
                class: "h3 section-title",
                "aria-label": section.SectionTitle,
            },
            [],
            section.SectionTitle
        );
        pageContent.appendChild(sectionTitle);

        if (section.SectionTitle == 'Excluding Yourself from the Settlement') {
            section.SectionLead = '<p>If you are a Settlement Class member and want to keep any right you may have to sue or continue to sue the Defendant and/or the other Released Parties on your own based on the claims raised in this Action or released by the Released Claims, then you must take steps to get out of the Settlement. This is called excluding yourself from—or “opting out” of—the Settlement. Any Class Member who does not file a timely Request for Exclusion in accordance with the instructions below will lose the opportunity to exclude himself or herself from the Settlement and will be bound by the Settlement.</p>';
        }

        const sectionLead = createElement(
            "p",
            { class: "sectionLead" },
            [],
            section.SectionLead
        );
        section.SectionLead ? pageContent.appendChild(sectionLead) : null;

        const sectionFaqs = createElement("div", {
            class: "accordion accordion-flush mb-4 section-faqs",
        });
        pageContent.appendChild(sectionFaqs);

        // The loop for the FAQs sections while maintaining the list count
        listCount = renderSection(
            section,
            sectionCount,
            sectionFaqs,
            listCount
        );
    });
}

/**
 * Loops through the sections and creates accordion items for each FAQ.
 * @param {Object} section - The section object.
 * @param {number} sectionCount - The count of the section.
 * @param {HTMLElement} sectionFaqs - The container element for the section FAQs.
 */
export function renderSection(section, sectionCount, sectionFaqs, listCount) {
    section.SectionFAQs.forEach((faq) => {
        listCount++;

        const accordionItem = createElement("div", { class: "accordion-item" });

        const accordionHeader = createElement("div", {
            class: "h3 accordion-header",
            id: `flush-heading-${listCount}`,
        });
        accordionItem.appendChild(accordionHeader);

        const accordionButton = createElement(
            "button",
            {
                class: "accordion-button lh-base fw-medium collapsed",
                type: "button",
                "data-bs-toggle": "collapse",
                "data-bs-target": `#flush-collapse-${listCount}`,
                "aria-expanded": "false",
                "aria-controls": `flush-collapse-${listCount}`,
                // }, [], `<span class="badge text-bg-light me-3">` + listCount + `</span> ` + faq.Question);
            },
            [],
            listCount + ". " + faq.Question
        );
        accordionHeader.appendChild(accordionButton);

        const permalinkToFaqs = `<br><a href="#"><i class="bi bi-link-45deg"></i> Permalink to FAQ</a>`;

        const accordionCollapse = createElement(
            "div",
            {
                id: `flush-collapse-${listCount}`,
                class: "accordion-collapse collapse",
                "aria-labelledby": `flush-heading-${listCount}`,
                "data-bs-parent": "#accordionFlushExample",
            },
            [],
            `<div class="accordion-body">${faq.Answer}</div>`
        );
        accordionItem.appendChild(accordionCollapse);

        sectionFaqs.appendChild(accordionItem);
    });
    return listCount;
}

/**
 * Function to handle FAQ linking.
 * It retrieves the 'id' parameter from the URL search parameters and scrolls to the corresponding FAQ element.
 */
export function faqLinking() {
    const urlSearch = new URLSearchParams(window.location.search);
    const id = urlSearch.get("id");
    if (id && id > 0) {
        const targetElement = document.querySelector(`#flush-heading-${id}`);
        targetElement.querySelector("button").classList.remove("collapsed");
        if (targetElement) {
            document
                .getElementById(`flush-collapse-${id}`)
                .classList.add("show");
            setTimeout(() => {
                targetElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 100);
        }
    }
}
